import { useGetRegionalResponsibleQuery, useGetAnalystsOptionsQuery } from "@/services/analyst-assignment/select-options";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { RoleEnum } from "@/enum/role";
import { Input } from "components/ui/input";
import { SelectInput } from "@/components/Select";
import { DateRangePicker, DateFilterType } from "components/ui/date-range-picker";
import { Loader2 } from 'lucide-react';
import React from "react";
import { useConciliationEmailsFilterActions } from "../filter/hooks/actions";
import { useFilterState } from "../filter/hooks/filterState";
import { ConciliationEmailReportType, EMAIL_TYPE_OPTIONS, ITEM_STATUS_OPTIONS, AGGREGATED_STATUS_OPTIONS } from "./conciliation-email-report-types";
import { filterClasses, getSelectClasses } from "@/utils/filter-styles";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatOptionLabel } from "@/utils/format-option-label";

interface ToolbarFiltersProps {
    listType: ConciliationEmailReportType;
}

export default function EmailReportToolbarFilters({ listType }: ToolbarFiltersProps) {
    const { updateFilter } = useConciliationEmailsFilterActions();
    const { filters } = useFilterState();
    const { data: storeOptions, isLoading: isLoadingStores } = useGetStoresOptionsQuery();
    const { data: regionalResponsibleOptions } = useGetRegionalResponsibleQuery();
    const { data: analystsOptions } = useGetAnalystsOptionsQuery({ role: RoleEnum.ANALISTA });

    const renderMultiSelect = (
        id: string,
        placeholder: string,
        options: { value: string | number; label: string }[] = [],
        value: any[],
        onChange: (value: any) => void
    ) => {
        const hasValue = value && value.length > 0;
        return (
            <div key={id} className={filterClasses.filterItem}>
                <SelectInput
                    options={options}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    className={getSelectClasses(hasValue)}
                    classNamePrefix="an-simple-select"
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ MultiValue: MultiValueTag }}
                    formatOptionLabel={formatOptionLabel}
                />
            </div>
        );
    };

    const renderSingleSelect = (
        id: string,
        placeholder: string,
        options: { value: string | number; label: string }[] = [],
        value: any,
        onChange: (value: any) => void
    ) => {
        const hasValue = value !== null && value !== undefined;
        return (
            <div key={id} className={filterClasses.filterItem}>
                <SelectInput
                    options={options}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    className={getSelectClasses(hasValue)}
                    classNamePrefix="an-simple-select"
                    isMulti={false}
                    isClearable
                    closeMenuOnSelect={true}
                    hideSelectedOptions={false}
                    formatOptionLabel={formatOptionLabel}
                />
            </div>
        );
    };

    const renderInput = (
        id: string,
        placeholder: string,
        value: string,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
        type: string = "text"
    ) => (
        <div key={id} className={filterClasses.filterItem}>
            <Input
                id={id}
                type={type}
                placeholder={placeholder}
                className="h-8 min-h-[32px] text-sm"
                value={value}
                onChange={onChange}
            />
        </div>
    );

    const renderDateRangePicker = (
        id: string,
        placeholder: string,
        onChange: (startDate: string | undefined, endDate: string | undefined) => void
    ) => (
        <div key={id} className={filterClasses.filterItem}>
            <DateRangePicker
                onDateChange={onChange}
                initialDateRange={getInitialDateRange()}
                placeholder={placeholder}
            />
        </div>
    );

    const renderDateRangePickerWithTypeSelector = (
        id: string,
        placeholder: string,
        onChange: (startDate: string | undefined, endDate: string | undefined, dateType?: DateFilterType) => void
    ) => (
        <div key={id} className={filterClasses.filterItem}>
            <DateRangePicker
                onDateChange={onChange}
                initialDateRange={getInitialDateRange()}
                initialDateType={(filters?.dateType as DateFilterType) || DateFilterType.EMAIL_SENT_AT}
                placeholder={placeholder}
                showDateTypeSelector={true}
            />
        </div>
    );

    // Handlers
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFilter({ key: "search", value: e.target.value });
    };

    const handleStoreChange = (selectedOptions: any) => {
        const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
        updateFilter({ key: "storeCode", value: values });
    };

    const handleRegionalChange = (selectedOptions: any) => {
        const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
        updateFilter({ key: "regional", value: values });
    };

    const handleTypeEmailChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : null;
        updateFilter({ key: "type", value });
    };

    const handleStatusChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : null;
        updateFilter({ key: "status", value });
    };

    const handleAggregatedStatusChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : null;
        updateFilter({ key: "aggregatedStatus", value });
    };

    const handleResponsibleChange = (selectedOption: any) => {
        const value = selectedOption ? selectedOption.value : null;
        updateFilter({ key: "responsible", value });
    };

    const handleOperatorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFilter({ key: "operator", value: e.target.value });
    };

    const handleDateRangeChange = (startDate: string | undefined, endDate: string | undefined) => {
        const fromValue = startDate ? new Date(startDate).toISOString() : "";
        const toValue = endDate ? new Date(endDate).toISOString() : "";

        updateFilter({ key: "from_date", value: fromValue });
        updateFilter({ key: "to_date", value: toValue });
    };

    const handleDateRangeChangeWithType = (startDate: string | undefined, endDate: string | undefined, dateType?: DateFilterType) => {
        const fromValue = startDate ? new Date(startDate).toISOString() : "";
        const toValue = endDate ? new Date(endDate).toISOString() : "";

        updateFilter({ key: "from_date", value: fromValue });
        updateFilter({ key: "to_date", value: toValue });
        if (dateType) {
            updateFilter({ key: "dateType", value: dateType });
        }
    };

    // Get initial date range from filters
    const getInitialDateRange = () => {
        if (!filters?.from_date && !filters?.to_date) return undefined;

        try {
            return {
                from: filters.from_date ? new Date(filters.from_date) : undefined,
                to: filters.to_date ? new Date(filters.to_date) : undefined,
            };
        } catch {
            return undefined;
        }
    };

    // Convert stored values to react-select format
    const getSelectedStores = () => {
        if (!filters?.storeCode) return [];
        const codes = Array.isArray(filters.storeCode) ? filters.storeCode : [filters.storeCode];
        return codes.map(code => storeOptions?.find(opt => String(opt.value) === String(code))).filter(Boolean);
    };

    const getSelectedRegionals = () => {
        if (!filters?.regional) return [];
        const regionals = Array.isArray(filters.regional) ? filters.regional : [filters.regional];
        return regionals.map(reg => regionalResponsibleOptions?.find(opt => String(opt.value) === String(reg))).filter(Boolean);
    };

    const getSelectedType = () => {
        if (!filters?.type) return null;
        return EMAIL_TYPE_OPTIONS?.find(opt => String(opt.value) === String(filters.type)) || null;
    };

    const getSelectedStatus = () => {
        if (!filters?.status) return null;
        return ITEM_STATUS_OPTIONS?.find(opt => String(opt.value) === String(filters.status)) || null;
    };

    const getSelectedAggregatedStatus = () => {
        if (!filters?.aggregatedStatus) return null;
        return AGGREGATED_STATUS_OPTIONS?.find(opt => String(opt.value) === String(filters.aggregatedStatus)) || null;
    };

    const getSelectedResponsible = () => {
        if (!filters?.responsible) return null;
        return analystsOptions?.find(opt => String(opt.value) === String(filters.responsible)) || null;
    };

    const FILTERS: Record<ConciliationEmailReportType, React.ReactNode[]> = {
        [ConciliationEmailReportType.EMAILS_BY_STORE]: [
            renderInput(
                "search",
                "Buscar...",
                filters?.search || "",
                handleSearchChange
            ),
            renderDateRangePicker(
                "date_range",
                "Selecionar período",
                handleDateRangeChange
            ),
            renderMultiSelect(
                "store",
                "Selecionar loja",
                storeOptions || [],
                getSelectedStores(),
                handleStoreChange
            ),
            renderMultiSelect(
                "regional",
                "Selecionar regional",
                regionalResponsibleOptions || [],
                getSelectedRegionals(),
                handleRegionalChange
            ),
        ],
        [ConciliationEmailReportType.EMAILS_BY_REGIONAL]: [
            renderInput(
                "search",
                "Buscar...",
                filters?.search || "",
                handleSearchChange
            ),
            renderDateRangePicker(
                "date_range",
                "Selecionar período",
                handleDateRangeChange
            ),
            renderMultiSelect(
                "regional",
                "Selecionar regional",
                regionalResponsibleOptions || [],
                getSelectedRegionals(),
                handleRegionalChange
            ),
        ],
        [ConciliationEmailReportType.DETAILED_DIVERGENCE]: [
            renderInput(
                "operator",
                "Buscar operador...",
                filters?.operator || "",
                handleOperatorChange
            ),
            renderDateRangePickerWithTypeSelector(
                "date_range",
                "Selecionar período",
                handleDateRangeChangeWithType
            ),
            renderMultiSelect(
                "store",
                "Selecionar loja",
                storeOptions || [],
                getSelectedStores(),
                handleStoreChange
            ),
            renderMultiSelect(
                "regional",
                "Selecionar regional",
                regionalResponsibleOptions || [],
                getSelectedRegionals(),
                handleRegionalChange
            ),
            renderSingleSelect(
                "responsible",
                "Selecionar responsável",
                analystsOptions || [],
                getSelectedResponsible(),
                handleResponsibleChange
            ),
            renderSingleSelect(
                "status",
                "Selecionar status",
                ITEM_STATUS_OPTIONS,
                getSelectedStatus(),
                handleStatusChange
            ),
        ],
        [ConciliationEmailReportType.DETAILED_NSU]: [
            renderInput(
                "operator",
                "Buscar operador...",
                filters?.operator || "",
                handleOperatorChange
            ),
            renderDateRangePickerWithTypeSelector(
                "date_range",
                "Selecionar período",
                handleDateRangeChangeWithType
            ),
            renderMultiSelect(
                "store",
                "Selecionar loja",
                storeOptions || [],
                getSelectedStores(),
                handleStoreChange
            ),
            renderMultiSelect(
                "regional",
                "Selecionar regional",
                regionalResponsibleOptions || [],
                getSelectedRegionals(),
                handleRegionalChange
            ),
            renderSingleSelect(
                "responsible",
                "Selecionar responsável",
                analystsOptions || [],
                getSelectedResponsible(),
                handleResponsibleChange
            ),
            renderSingleSelect(
                "status",
                "Selecionar status",
                ITEM_STATUS_OPTIONS,
                getSelectedStatus(),
                handleStatusChange
            ),
        ],
        [ConciliationEmailReportType.SENT_EMAILS]: [
            renderInput(
                "search",
                "Buscar...",
                filters?.search || "",
                handleSearchChange
            ),
            renderDateRangePickerWithTypeSelector(
                "date_range",
                "Selecionar período",
                handleDateRangeChangeWithType
            ),
            renderMultiSelect(
                "store",
                "Selecionar loja",
                storeOptions || [],
                getSelectedStores(),
                handleStoreChange
            ),
            renderMultiSelect(
                "regional",
                "Selecionar regional",
                regionalResponsibleOptions || [],
                getSelectedRegionals(),
                handleRegionalChange
            ),
            renderSingleSelect(
                "type",
                "Selecionar tipo de e-mail",
                EMAIL_TYPE_OPTIONS,
                getSelectedType(),
                handleTypeEmailChange
            ),
            renderSingleSelect(
                "aggregatedStatus",
                "Selecionar status",
                AGGREGATED_STATUS_OPTIONS,
                getSelectedAggregatedStatus(),
                handleAggregatedStatusChange
            ),
        ],
        [ConciliationEmailReportType.EMAILS_BY_CONCILIATION]: [
            renderInput(
                "search",
                "Buscar...",
                filters?.search || "",
                handleSearchChange
            ),
            renderDateRangePicker(
                "date_range",
                "Selecionar período",
                handleDateRangeChange
            ),
        ],
    };

    return (
        <div className={filterClasses.container}>
            {FILTERS[listType].map((el, i) => (
                <React.Fragment key={i}>{el}</React.Fragment>
            ))}
            {isLoadingStores && (
                <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
            )}
        </div>
    );
}